import React from 'react'
import { useTheme } from '@emotion/react'

import { CommonCardProps } from 'component-library/cards/CardItem.Props'
import { ImageWidths } from 'common/helpers/image'

import {
    StyledPromotionalCard,
    StyledLink,
    StyledCardText,
    StyledCTA,
    StyledCardMedia,
    StyledCardTextContainer,
} from 'component-library/cards/PromotionalCard/PromotionalCard.styled'

import { toLinkWithHint } from 'common/routing/route-hints'
import { FontScales } from 'component-library/__styling/settings/fontScale'
import { ThemeMargins } from 'component-library/__styling/settings/metrics'
import { themedValue } from 'component-library/__styling/themed-value'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import { FixedRatio } from 'common/client-data-types/image-item'
import { MediaMode } from '../CardMedia/CardMedia'
import { BreakpointKeys } from 'component-library/__styling/settings/breakpoints'
import { PublicationCardItem } from 'common/client-data-types/card-types'
import { createCardClickedEvent } from 'common/events/collection-events'
export type AvailableLayouts = 'landscape' | 'portrait'

export interface AlternativeLayoutOptions {
    breakpoint: BreakpointKeys
    layout: AvailableLayouts
}
export interface CardOrientationOptions {
    defaultLayout: AvailableLayouts
    alternativeLayoutOptions?: AlternativeLayoutOptions
}

export interface Props extends CommonCardProps {
    verticalSpacing?: keyof ThemeMargins
    /**
     * defaults to `4:3`
     */
    fixedRatio?: FixedRatio
    imageWidths?: ImageWidths['thewest'] | ImageWidths['perthnow'] | ImageWidths['sevennews']
    fontScale?: FontScales
    disableImageLazyLoad?: boolean
    hasPadding?: boolean
    showCTA?: boolean
    mediaMode?: MediaMode
    cardOrientation: CardOrientationOptions
    mobileOnly?: true

    cardNumber: number
    innerRef?: React.RefObject<any>

    item: MaybeLoaded<PublicationCardItem>
}

export const PromotionalCard: React.FC<Props> = ({
    className,
    item,
    verticalSpacing,
    fixedRatio,
    fontScale,
    imageWidths,
    hasPadding,
    disableImageLazyLoad,
    onEvent,
    mediaMode,
    cardOrientation,
    cardNumber,
    showCTA,
    innerRef,
    mobileOnly,
}) => {
    const theme = useTheme()
    const linkClicked = (e: React.MouseEvent<HTMLElement>) => {
        if (!item.loaded) {
            // We don't want the link to do anything
            e.preventDefault()
            return
        }

        onEvent(
            createCardClickedEvent(
                item.result,
                'InternalPromotionalCard',
                'PromotionalCard',
                cardNumber
            )
        )
    }

    const setFontScale = themedValue(theme, {
        thewest: fontScale || 0.9,
        perthnow: fontScale,
        sevennews: fontScale || 1.2,
    }) as FontScales

    return (
        <StyledPromotionalCard
            ref={innerRef}
            className={className}
            data-topic={item.loaded ? item.result.primaryTopic.id : undefined}
            verticalSpacing={verticalSpacing}
            isLoading={!item.loaded}
            mobileOnly={mobileOnly}
            cardOrientation={cardOrientation}
        >
            <StyledLink
                to={toLinkWithHint(item)}
                onClick={linkClicked}
                isLoading={!item.loaded}
                theme={theme}
                hasPadding={hasPadding}
                cardOrientation={cardOrientation}
            >
                <StyledCardMedia
                    item={item}
                    fixedRatio={fixedRatio}
                    imageWidths={imageWidths}
                    onEvent={onEvent}
                    disableImageLazyLoad={disableImageLazyLoad}
                    mediaMode={mediaMode}
                    cardOrientation={cardOrientation}
                />

                <StyledCardTextContainer cardOrientation={cardOrientation} isLoading={!item.loaded}>
                    <StyledCardText
                        item={item}
                        fontScale={setFontScale}
                        onEvent={onEvent}
                        hideTeaser={true}
                    />

                    {showCTA && (
                        <StyledCTA cardOrientation={cardOrientation}>Find out More</StyledCTA>
                    )}
                </StyledCardTextContainer>
            </StyledLink>
        </StyledPromotionalCard>
    )
}
PromotionalCard.displayName = 'PromotionalCard'
